import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { setupAxios } from './helpers/httpRequestHelper';
import { QuestionsScene } from './scenes/QuestionsScene/QuestionsScene';
import { AnnouncementsScene } from './scenes/AnnouncementsScene/AnnouncementsScene';
import { createTheme } from './styles/theme';
import { ThemeProvider } from 'styled-components';
import { logger } from './logger';
import { verifyAppConfiguration } from './helpers/verifyAppConfiguration';
import { BackwardCompatibleAppConfiguration, LegacyQuestionState } from './models/AppConfiguration';
import { logRequestAndResponseDetails } from './api/utils/logRequestAndResponseDetails';
import axios from 'axios';
import { newRelicAgent } from './utils/newrelic';
import { trackError, ErrorBoundary } from './utils/errorTracking';

const isLegacyQuestionState = (state: any): state is LegacyQuestionState => !('answers' in state);

const convertLegacyAppConfig = (config: BackwardCompatibleAppConfiguration) => {
  return {
    ...config,
    initialState: {
      ...config.initialState,
      questionResponses: config.initialState.questionResponses.map(qr =>
        isLegacyQuestionState(qr) ? { ...qr, answers: qr.values.map(value => ({ value })), values: undefined } : qr
      )
    }
  };
};

// Set up global error handlers
window.onerror = (message, source, lineno, colno, error) => {
  trackError({
    error: error || (message instanceof Error ? message : new Error(String(message))),
    componentName: 'window.onerror',
    additionalData: { source, lineno, colno }
  });
};

window.onunhandledrejection = event => {
  trackError({
    error: event.reason,
    componentName: 'window.onunhandledrejection',
    additionalData: { type: 'unhandled-promise-rejection' }
  });
};

const containerMap = new WeakMap<HTMLElement, Root>();

const renderScene = (sceneName: 'questions' | 'announcements', userConfig: BackwardCompatibleAppConfiguration) => {
  newRelicAgent.recordCustomEvent('SceneInitialization', {
    sceneName,
    environment: userConfig.environment,
    buildVersion: process.env.GIT_SHA?.substr(0, 7) || 'localhost',
    buildTime: process.env.BUILD_TIMESTAMP
  });

  logger.interceptor = log => ({
    ...log,
    caiApp: 'qna-ui',
    caiEnvironment: userConfig.environment,
    meta: {
      location: window.location.href,
      sceneName,
      config: userConfig,
      build: process.env.GIT_SHA?.substr(0, 7) || 'localhost',
      buildTime: process.env.BUILD_TIMESTAMP
    }
  });

  logger.info('app:initialized');
  const Scene = sceneName === 'questions' ? QuestionsScene : AnnouncementsScene;
  const config = convertLegacyAppConfig(userConfig);

  try {
    verifyAppConfiguration(config);
  } catch (error) {
    trackError({
      error: error instanceof Error ? error : new Error(String(error)),
      componentName: 'verifyAppConfiguration',
      additionalData: { config: JSON.stringify(config) }
    });
    throw error;
  }

  setupAxios(config);

  const container = document.getElementById(config.rootElementId);
  if (!container) {
    const error = new Error(`Root element not found: ${config.rootElementId}`);
    trackError({
      error,
      componentName: 'renderScene',
      additionalData: { rootElementId: config.rootElementId }
    });
    logger.error('app:initialize', { error });
    return;
  }

  let rootElement = containerMap.get(container);
  if (!rootElement) {
    rootElement = createRoot(container);
    containerMap.set(container, rootElement);
  }

  newRelicAgent.recordCustomEvent('SceneRender', {
    sceneName,
    environment: userConfig.environment,
    rootElementId: config.rootElementId
  });

  rootElement.render(
    <ErrorBoundary componentName={`${sceneName}Scene`}>
      <ThemeProvider theme={createTheme(config.colors)}>
        <Scene {...config} />
      </ThemeProvider>
    </ErrorBoundary>
  );
};

logRequestAndResponseDetails(axios);
export const QuestionsAndAnnouncements = {
  renderQuestions: (userConfig: BackwardCompatibleAppConfiguration) => {
    renderScene('questions', userConfig);
  },
  renderAnnouncements: (userConfig: BackwardCompatibleAppConfiguration) => {
    renderScene('announcements', userConfig);
  }
};

Object.assign(window, { QuestionsAndAnnouncements });
